import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    Link,
    Divider,
    CircularProgress,
    FormControlLabel,
    Checkbox,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import QRCode from 'react-qr-code';
import TemplateSelectionDialog, { type TemplateSelectionMode } from './TemplateSelectionDialog';
import { buildMultiLabelPrintHtml } from './PrintTemplate';
import { isHorizontal as isHorizontalOrientation, computePreviewMetrics } from './labelSizeConfig';
import type { LabelTemplate } from '../../api/vegApl/labelTemplate';
import { printLabelDialogStyles as styles } from '../../styles/MtComStyleObj';
import { parseLabelDataPoints, splitTextFields } from '../../utils/LabelFieldMapper';
import { printViaIframe, extractQrSvgHtml, onPrintComplete } from '../../utils/printViaIframe';
import { useCommonStyles } from '../../styles/GenericStyle';
import { GenericConstant } from '../../utils/GenericConstant';

export interface InfoField {
    label: string;
    value: string;
}

interface PrintSPLotLabelDialogProps {
    open: boolean;
    onClose: () => void;
    onPrint: (templateName: string) => void;
    onAddNewTemplate?: () => void;
    rowData: Record<string, any>;
    hasTemplate?: boolean;
    foundTemplate?: LabelTemplate | null;
    splotTemplates?: LabelTemplate[];
    /** Dialog title, e.g. "Print Material Label". Defaults to "Print SP Lot Label" */
    dialogTitle?: string;
    /** Primary ID label, e.g. "Material ID". Defaults to "SP Lot ID" */
    primaryIdLabel?: string;
    /** Primary ID value. Defaults to rowData.splot_id */
    primaryIdValue?: string;
    /** Additional info fields shown between primary ID and template box */
    infoFields?: InfoField[];
    /** Whether this is a new container (true = "Print Label", false = "Reprint Label") */
    isNewContainer?: boolean;
    /** Show register-to-mint toggle in dialog footer */
    showRegisterToMintToggle?: boolean;
    /** Current register-to-mint value */
    registerToMint?: boolean;
    /** Register-to-mint change handler */
    onRegisterToMintChange?: (checked: boolean) => void;
    /** Don't persist register-to-mint selection (always reset on close) */
    dontPersistRegisterToMint?: boolean;
    /** Register-to-mint label text */
    registerToMintLabel?: string;
}

const PrintSPLotLabelDialog = ({
    open,
    onClose,
    onPrint,
    onAddNewTemplate,
    rowData,
    hasTemplate = true,
    foundTemplate = null,
    splotTemplates = [],
    dialogTitle = 'Print SP Lot Label',
    primaryIdLabel = 'SP Lot ID',
    primaryIdValue,
    infoFields,
    isNewContainer = true,
    showRegisterToMintToggle = false,
    registerToMint = true,
    onRegisterToMintChange,
    registerToMintLabel = 'Trigger MINT registration after label printing',
}: PrintSPLotLabelDialogProps) => {
    const classes = useCommonStyles();
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [activeTemplate, setActiveTemplate] = useState<{ name: string; size: string; orientation: string; data_points?: string; default_count?: number } | null>(null);
    const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
    const [templateDialogMode, setTemplateDialogMode] = useState<TemplateSelectionMode>('noTemplate');
    const [pendingTemplate, setPendingTemplate] = useState('');
    const [isPrinting, setIsPrinting] = useState(false);
    const labelRef = useRef<HTMLDivElement>(null);

    const templateSize = activeTemplate?.size ?? foundTemplate?.size ?? '';
    const templateOrientation = activeTemplate?.orientation ?? foundTemplate?.orientation ?? 'Horizontal';
    // Determine if this is Material mode (has info fields) or SPLot mode (no info fields)
    const isMaterialMode: boolean = Boolean(infoFields && infoFields.length > 0);

    // Parse data_points from multiple sources (priority: activeTemplate > template from validation > rowData.data_points > '')
    const dataPoints = activeTemplate?.data_points ?? foundTemplate?.data_points ?? rowData?.data_points ?? '';
    const { qrFields, textFields } = useMemo(
        () => parseLabelDataPoints(dataPoints, rowData, isMaterialMode),
        [dataPoints, rowData, isMaterialMode],
    );

    const isVerticalTemplate = !!templateOrientation && templateOrientation.toLowerCase().includes('vertical');
    
    // Use textFields as parsed from data_points directly (no additional filtering)
    // The data_points template definition determines what appears on the label
    const filteredTextFields = textFields;

    // Layout always follows actual template orientation; material vertical stays stacked one below the other
    const horizontal = isHorizontalOrientation(templateOrientation);

    // For material vertical labels, compute font sizes using horizontal metrics to match horizontal label sizing
    const fontMetricsOrientation = (isMaterialMode && isVerticalTemplate) ? 'Horizontal' : templateOrientation;

    const { preview, qrSize } = useMemo(
        () => computePreviewMetrics(templateSize, templateOrientation, qrFields.length, filteredTextFields.length, isMaterialMode),
        [templateSize, templateOrientation, qrFields.length, filteredTextFields.length, isMaterialMode],
    );
    const { fontSize } = useMemo(
        () => computePreviewMetrics(templateSize, fontMetricsOrientation, qrFields.length, filteredTextFields.length, isMaterialMode),
        [templateSize, fontMetricsOrientation, qrFields.length, filteredTextFields.length, isMaterialMode],
    );

    const { topText, bottomText } = useMemo(
        () => splitTextFields(filteredTextFields),
        [filteredTextFields],
    );

    // Admin Code / Trial ID keep their larger fixed size unless the width left beside the
    // bottom-row QR can't hold them (small near-square labels), in which case they scale down.
    const adminCodeFontPx = useMemo(() => {
        const MM_TO_PX = 3.78;
        const basePx = 15.2; // styles.adminCodeLabel 0.95rem
        const occupiedPx = (2 * MM_TO_PX) + (qrFields.length > 1 ? qrSize + (1.5 * MM_TO_PX) : 0);
        const availablePx = Math.max(0, preview.width - occupiedPx);
        return Math.min(basePx, availablePx / (18 * 0.72));
    }, [preview.width, qrSize, qrFields.length]);

    // Derive crop/site once — used in template subtitle and TemplateSelectionDialog
    const cropName = rowData.cropName ?? rowData.crop ?? '';
    const cropCode = foundTemplate?.crop_code ?? rowData.crop_code ?? rowData.crop ?? '';
    const siteName = rowData.site_code ?? rowData.site ?? rowData.template?.site_code ?? '';
    const materialStatus = String(rowData?.["Current Status"] ?? '').trim().toLowerCase();

    // When opened, if template found set it; if not found show the "No Template Found" dialog
    useEffect(() => {
        if (open && hasTemplate && foundTemplate) {
            setSelectedTemplate(foundTemplate.template_name ?? '');
            setActiveTemplate({ name: foundTemplate.template_name ?? '', size: foundTemplate.size ?? '', orientation: foundTemplate.orientation ?? 'Horizontal', data_points: foundTemplate.data_points ?? '', default_count: foundTemplate.default_count });
            setTemplateDialogOpen(false);
        } else if (open && !hasTemplate) {
            setActiveTemplate(null);
            setTemplateDialogMode('noTemplate');
            setTemplateDialogOpen(true);
        }
    }, [open, hasTemplate, foundTemplate]);

    const onRegisterToMintChangeRef = useRef(onRegisterToMintChange);
    onRegisterToMintChangeRef.current = onRegisterToMintChange;

    useEffect(() => {
        if (open) {
            if (showRegisterToMintToggle && materialStatus !== 'registered') {
                onRegisterToMintChangeRef.current?.(true);
            } else if (!showRegisterToMintToggle || materialStatus === 'registered') {
                onRegisterToMintChangeRef.current?.(false);
            }
        }
    }, [open, showRegisterToMintToggle, materialStatus]);

    const handleChangeTemplate = useCallback(() => {
        setPendingTemplate('');
        setTemplateDialogMode('changeTemplate');
        setTemplateDialogOpen(true);
    }, []);

    const handleTemplateConfirm = (templateId: string) => {
        const matched = splotTemplates.find((t) => String(t.template_id) === templateId);
        if (matched) {
            setSelectedTemplate(matched.template_name);
            setActiveTemplate({ name: matched.template_name, size: matched.size, orientation: matched.orientation, data_points: matched.data_points ?? '', default_count: matched.default_count });
        } else {
            setSelectedTemplate(templateId);
        }
        setTemplateDialogOpen(false);
    };

    const handleTemplateDialogClose = () => {
        setTemplateDialogOpen(false);
        if (templateDialogMode === 'noTemplate' && !hasTemplate) {
            onClose();
        }
    };

    const handleClose = useCallback(() => {
        setSelectedTemplate('');
        setActiveTemplate(null);
        setPendingTemplate('');
        onClose();
        onRegisterToMintChange?.(true);
    }, [onClose, onRegisterToMintChange]);

    const handlePrint = () => {
        if (!labelRef.current || isPrinting) return;
        setIsPrinting(true);


        // In SPLot mode, ensure the primary ID is available as a QR field so the
        // print builder can render both the QR code and the ID text.
        // (When a template has no "QRCode for" data-point the qrFields array is empty
        //  but the preview still renders the QR from primaryIdValue, so the extracted
        //  SVG will be present — we just need to supply the corresponding field.)
        const effectiveQrFields =
            !isMaterialMode && qrFields.length === 0
                ? [{ key: 'splot_id', label: 'SP Lot ID', value: primaryIdValue ?? rowData.splot_id ?? '' }]
                : qrFields;

        try {
            const qrSvgHtml = extractQrSvgHtml(labelRef.current);
            // Get copies per page from API default_count
            const copiesPerPage = activeTemplate?.default_count ?? foundTemplate?.default_count ?? GenericConstant.LABELS_PER_PRINT_PAGE ?? 6;
            // Build multi-label print HTML with configured copies per page
            const html = buildMultiLabelPrintHtml({
                labels: [{
                    qrFields: effectiveQrFields,
                    textFields: filteredTextFields,
                    qrSvgHtml,
                }],
                size: templateSize,
                orientation: templateOrientation,
                isMaterialMode,
                copiesPerPage,
                leftMarginMm: isMaterialMode ? 0 : 90,
            });

            printViaIframe(html);
            onPrintComplete(() => {
                setIsPrinting(false);
                onPrint(selectedTemplate);
            });
        } catch {
            setIsPrinting(false);
        }
    };

    // const copiesCount = isMaterialMode ? 1 : (activeTemplate?.default_count ?? foundTemplate?.default_count ?? GenericConstant.LABELS_PER_PRINT_PAGE ?? 6);

    const labelBodyJsx = horizontal && !isMaterialMode ? (
        /* SPLot single-row horizontal layout: QR left + text right, occupies full height */
        (() => {
            // Use QR field value if available, fall back to the explicit primaryIdValue or rowData
            const splotId = qrFields[0]?.value || primaryIdValue || rowData.splot_id || '';
            return (
                <Box sx={{ ...styles.labelRow, flex: 1, p: 0, gap: 0.5, height: '100%' }}>
                    {splotId && (
                        <Box sx={{ ...styles.qrCell, width: '25px', height: '25px', maxWidth: '42%' }}>
                            <QRCode value={splotId} size={qrSize} />
                        </Box>
                    )}
                    <Box sx={{ ...styles.textCell, gap: 0, height: '100%' }}>
                        {splotId && (
                            <Typography sx={{ ...styles.splotIdLabel, fontSize: fontSize.title }}>{splotId}</Typography>
                        )}
                        {filteredTextFields.map((f) => {
                            const isAdminCode = f.key.toLowerCase().replace(/[\s_]/g, '') === 'admincode';
                            return <Typography key={f.key} sx={{ ...(isAdminCode ? styles.adminCodeLabel : styles.originLabel), fontSize: fontSize.origin }}>{f.value}</Typography>;
                        })}
                    </Box>
                </Box>
            );
        })()
    ) : horizontal ? (
        /* Material four-row horizontal layout */
        <>
            {/* Row 1: Material ID banner */}
            <Box sx={{ ...styles.labelRow, p: '1mm', alignItems: 'center' }}>
                {qrFields.length > 0 && (
                    <Typography sx={{ ...styles.splotIdLabel, fontSize: fontSize.title, lineHeight: 1.2, fontWeight: 700 }}>
                        {qrFields[0].value}
                    </Typography>
                )}
            </Box>
            {/* Row 2: MatID QR + Site/Crop/Sector */}
            <Box sx={{ ...styles.labelRow, flex: 1, p: '1mm', gap: '1.5mm', alignItems: 'center' }}>
                {qrFields.length > 0 && (
                    <Box sx={{ ...styles.qrCell, width: qrSize, height: qrSize, flexShrink: 0 }}>
                        <QRCode value={qrFields[0].value || ' '} size={qrSize} />
                    </Box>
                )}
                <Box sx={{ ...styles.textCell, gap: 0 }}>
                    {topText.map((f) => {
                        const normKey = f.key.toLowerCase().replace(/[\s_]/g, '');
                        const isAdminCode = normKey === 'admincode' || normKey === 'trialid' || normKey === 'trailid' || normKey === 'trialbid' || normKey === 'traidbid' || normKey === 'traidid';
                        return <Typography key={f.key} sx={{ ...(isAdminCode ? styles.adminCodeLabel : styles.originLabel), ...(isAdminCode ? {} : { fontSize: fontSize.origin }), lineHeight: 1.2 }}>{f.value || '\u00A0'}</Typography>;
                    })}
                </Box>
            </Box>
            {/* Row 3: Treatments + Container QR */}
            <Box sx={{ ...styles.labelRow, flex: 1, p: '1mm', gap: '1.5mm', alignItems: 'flex-start' }}>
                <Box sx={{ ...styles.textCellFlex, gap: 0 }}>
                    {bottomText.map((f) => {
                        const normKey = f.key.toLowerCase().replace(/[\s_]/g, '');
                        const isAdminCode = normKey === 'admincode' || normKey === 'trialid' || normKey === 'trailid' || normKey === 'trialbid' || normKey === 'traidbid' || normKey === 'traidid';
                        return <Typography key={f.key} sx={{ ...(isAdminCode ? styles.adminCodeLabel : styles.footerLabel), fontSize: isAdminCode ? adminCodeFontPx : fontSize.footer, lineHeight: 1.15 }}>{f.value || '\u00A0'}</Typography>;
                    })}
                </Box>
                {qrFields.length > 1 && (
                    <Box sx={{ ...styles.qrCell, width: qrSize, height: qrSize, flexShrink: 0 }}>
                        <QRCode value={qrFields[1].value || ' '} size={qrSize} />
                    </Box>
                )}
            </Box>
            {/* Row 4: Container ID footer */}
            <Box sx={{ ...styles.labelRow, p: '1mm', justifyContent: 'flex-end', alignItems: 'center' }}>
                {qrFields.length > 1 && (
                    <Typography sx={{ ...styles.splotIdLabel, fontSize: fontSize.title, lineHeight: 1.2, fontWeight: 700 }}>
                        {qrFields[1].value}
                    </Typography>
                )}
            </Box>
        </>
    ) : (
        /* Vertical layout: QR on top, text stacked center, optional QR at bottom */
        <Box sx={{ ...styles.verticalWrapper }}>
            {qrFields.length > 0 && (
                <Box sx={{ ...styles.verticalQrCell, width: qrSize, height: qrSize }}>
                    <QRCode value={qrFields[0].value || ' '} size={qrSize} />
                </Box>
            )}
            <Box sx={{ ...styles.verticalTextGroup, gap: 0, flex: 1, justifyContent: 'center', width: '100%' }}>
                {qrFields.length > 0 && (
                    <Typography sx={{ ...styles.splotIdLabel, ...styles.verticalTextLabel, fontSize: fontSize.title, lineHeight: 1.4 }}>
                        {qrFields[0].value}
                    </Typography>
                )}
                {filteredTextFields.map((f) => {
                    const normKey = f.key.toLowerCase().replace(/[\s_]/g, '');
                    const isAdminCode = normKey === 'admincode' || normKey === 'trialid' || normKey === 'trailid' || normKey === 'trialbid' || normKey === 'traidbid' || normKey === 'traidid';
                    return <Typography key={f.key} sx={{ ...(isAdminCode ? styles.adminCodeLabel : styles.originLabel), ...styles.verticalTextLabel, ...(isAdminCode ? {} : { fontSize: fontSize.origin }) }}>
                        {f.value}
                    </Typography>;
                })}
            </Box>
            {qrFields.length > 1 && (
                <>
                    <Box sx={{ ...styles.verticalQrCell, width: qrSize, height: qrSize }}>
                        <QRCode value={qrFields[1].value || ' '} size={qrSize} />
                    </Box>
                    <Typography sx={{ ...styles.splotIdLabel, ...styles.verticalTextLabel, fontSize: fontSize.title, lineHeight: 1.4 }}>
                        {qrFields[1].value}
                    </Typography>
                </>
            )}
        </Box>
    );

    return (
        <>
            <Dialog open={open && !templateDialogOpen} onClose={handleClose} maxWidth={false} PaperProps={{ sx: { ...styles.paper, position: 'relative' } }}>
                {isPrinting && (
                    <Box sx={styles.printingOverlay}>
                        <CircularProgress size={40} />
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            Preparing label for print…
                        </Typography>
                    </Box>
                )}

                <DialogContent sx={{ p: 0 }}>
                    <Box sx={styles.contentLayout}>
                        {/* Left Section */}
                        <Box sx={styles.leftPanel}>
                            <Typography variant="h6" sx={styles.titleText}>{dialogTitle}</Typography>
                            <Divider sx={{ mb: 2, mx: -3 }} />
                            <Typography variant="body1" sx={styles.splotIdText}>{primaryIdLabel} : {primaryIdValue ?? rowData.splot_id ?? ''}</Typography>

                            {infoFields && infoFields.length > 0 ? (
                                /* Material mode — show info fields */
                                <Box sx={styles.infoFieldsBox}>
                                    {infoFields.map((field, idx) => (
                                        <Box key={idx} sx={styles.infoFieldItem}>
                                            <Typography variant="body2" sx={styles.infoFieldLabel}>{field.label}</Typography>
                                            <Typography variant="body2" sx={styles.infoFieldValue}>{field.value}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                /* SP Lot mode — no extra info fields */
                                null
                            )}

                            <Box sx={styles.templateBox}>
                                <Typography variant="body1" sx={styles.templateTitle}>
                                    Auto-selected Template: {selectedTemplate}
                                </Typography>
                                <Typography variant="body2" sx={styles.templateSubtitle}>
                                    For {cropName} - {siteName}
                                </Typography>
                            </Box>
                            <Link component="button" variant="body2" onClick={handleChangeTemplate} sx={styles.changeLink}>
                                Change Template
                            </Link>
                        </Box>

                        {/* Right Section - Label Preview */}
                        <Box sx={styles.rightPanel}>
                            <Typography variant="body2" sx={styles.labelCaption}>
                                Label : {activeTemplate?.name ?? selectedTemplate} {templateOrientation}
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, overflowY: 'auto', maxHeight: 520 }}>
                                {Array.from({ length: 1 }).map((_, i) => (
                                    <Box
                                        key={i}
                                        ref={i === 0 ? labelRef : undefined}
                                        sx={{
                                            ...styles.labelContainer,
                                            gridTemplateRows: horizontal && !isMaterialMode ? '1fr' : horizontal && isMaterialMode ? 'auto 1fr 1fr auto' : '1fr',
                                            width: { xs: '100%', sm: preview.width },
                                            height: { xs: 'auto', sm: horizontal && !isMaterialMode ? '100px' : preview.height },
                                            minHeight: { xs: 160, sm: preview.height },
                                            aspectRatio: { xs: `${preview.width} / ${preview.height}`, sm: 'unset' },
                                            padding: isMaterialMode ? '8px' : '0px',
                                        }}
                                    >
                                        {labelBodyJsx}
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions sx={styles.actions}>
                    {(() => {
                        if (!showRegisterToMintToggle) {
                            return null;
                        }

                        if (materialStatus === 'registered') {
                            return null;
                        }

                        return (
                            <FormControlLabel
                                sx={{ mr: 'auto' }}
                                control={
                                    <Checkbox
                                        checked={registerToMint}
                                        onChange={(e) => onRegisterToMintChange?.(e.target.checked)}
                                        sx={GenericConstant.CHECKBOXSTYLE}
                                    />
                                }
                                label={<Typography className={classes.labelNoto16Regular400}>{registerToMintLabel}</Typography>}
                            />
                        );
                    })()}
                    <Button onClick={handleClose} variant="outlined" disableRipple sx={styles.cancelBtn}>Cancel</Button>
                    <Button onClick={handlePrint} variant="contained" className={classes.greenButton} startIcon={<PrintIcon />} disableRipple sx={styles.printBtn} disabled={isPrinting}>
                        {isNewContainer ? 'Print Label' : 'Reprint Label'}
                    </Button>
                </DialogActions>
            </Dialog>

            <TemplateSelectionDialog
                open={templateDialogOpen}
                onClose={handleTemplateDialogClose}
                onConfirm={handleTemplateConfirm}
                onAddNewTemplate={onAddNewTemplate}
                mode={templateDialogMode}
                cropName={cropName}
                cropCode={cropCode}
                siteName={siteName}
                templateOptions={splotTemplates}
                selectedTemplate={pendingTemplate}
                onTemplateChange={setPendingTemplate}
                noTemplateTitle={dialogTitle}
            />
        </>
    );
};

export default PrintSPLotLabelDialog;
